import React from 'react';
import { useTranslation } from 'react-i18next';
import { CaretSortIcon, CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { UnstyledButton } from '@/shared/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/components/shadcn/dropdown-menu';

/** The direction a list walks its records: newest first, or oldest first. */
export type ListOrder = 'desc' | 'asc';

/** The two options, in the order the menu lists them. */
const OPTIONS: ListOrder[] = ['desc', 'asc'];

interface PageHeaderSortMenuProps {
  /** The active direction. */
  order: ListOrder;
  /** Called with the chosen direction when a menu option is selected. */
  onChange: (next: ListOrder) => void;
  /** Where the trigger sits, for the test id. */
  testIdPrefix?: string;
}

/**
 * The "Ordenar" dropdown that sits beside the Filtros button in the search row.
 *
 * Both listings -- clients and properties -- order by when the record was
 * created, so the control is one component with one vocabulary rather than
 * two headers inventing their own words for the same two directions. The
 * trigger mirrors the group-by menu's, which mirrors the Filtros button, so
 * the three read as one control family.
 *
 * Descending is the default everywhere, and the trigger stays in its quiet
 * state for it: an affordance that renders as "active" on a screen nobody
 * configured reads as a filter the broker forgot to clear.
 *
 * @param props - The active order and the change handler.
 * @returns The ordering dropdown control.
 */
export const PageHeaderSortMenu: React.FC<PageHeaderSortMenuProps> = ({ order, onChange, testIdPrefix = 'list' }) => {
  const { t } = useTranslation('common');
  const isActive = order !== 'desc';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <UnstyledButton
          type="button"
          data-testid={`${testIdPrefix}-sort-button`}
          aria-label={t('ordering.ariaLabel', { label: t(`ordering.options.${order}`) })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-medium)' as unknown as number,
            background: isActive ? 'var(--selection-active)' : 'transparent',
            color: isActive ? 'var(--seed-base)' : 'var(--text-secondary)',
            border: isActive ? 'none' : '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            whiteSpace: 'nowrap',
          }}
        >
          <CaretSortIcon width={14} height={14} />
          {t(`ordering.options.${order}`)}
          <ChevronDownIcon width={14} height={14} />
        </UnstyledButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel
          className="font-(--font-semibold)"
          style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}
        >
          {t('ordering.menuTitle')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((value) => {
          const selected = value === order;
          return (
            <DropdownMenuItem
              key={value}
              data-testid={`${testIdPrefix}-sort-option-${value}`}
              aria-checked={selected}
              role="menuitemradio"
              onSelect={() => onChange(value)}
              style={{
                color: selected ? 'var(--seed-base)' : 'var(--text-primary)',
                fontWeight: selected
                  ? ('var(--font-semibold)' as unknown as number)
                  : ('var(--font-medium)' as unknown as number),
              }}
            >
              {t(`ordering.options.${value}`)}
              {selected && <CheckIcon width={15} height={15} style={{ marginLeft: 'auto' }} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
